/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiPlus,
  FiBox,
  FiEdit2,
  FiEye,
  FiTrash2,
  FiX,
  FiUploadCloud,
  FiTag,
  FiPackage,
  FiActivity

} from "react-icons/fi";
import Swal from "sweetalert2";
import PageContainer from "../components/PageContainer";
import UnifiedCard from "../components/UnifiedCard";
import ResponsiveTable from "../components/ResponsiveTable";
import { useUI } from "../theme/ThemeProvider";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import NoAccess from "../components/NoAccess";




export default function AdminAllProducts() {
  const navigate = useNavigate();
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const perms = useTenantStorePermissions();
  const getItemId = (item) =>
    item?.itemId ??
    item?.ItemId ??
    item?.id ??
    null;

  const calculatePriceAfterDiscount = (originalPrice, discountValue, discountType) => {
    const price = parseFloat(originalPrice) || 0;
    const discount = parseFloat(discountValue) || 0;
    let finalPrice = price;

    if (discountType === 1) { // Percentage %
      finalPrice = price - (price * (discount / 100));
    } else { // Fixed Amount
      finalPrice = price - discount;
    }
    return finalPrice;
  };

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [stockMap, setStockMap] = useState(new Map()); // Store stock data
  const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, "");
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
  const baseImageUrl = API_BASE.replace("/api", "");

  useEffect(() => {
    const token = localStorage.getItem("authToken");

    Promise.all([
      // ===== TAGS =====
      fetch(`${API_BASE}/Tags/get?tenantId=${tenantId}`, {
        headers: {
          Accept: "*/*",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).then((r) => r.json()),

      // ===== CATEGORIES =====
      fetch(`${API_BASE}/TenantCategory/GetCategories?TenantId=${tenantId}`, {
        headers: {
          Accept: "*/*",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).then((r) => r.json()),
    ])
      .then(([tagsData, categoriesData]) => {
        // ===== NORMALIZE TAGS =====
        const normalizedTags = Array.isArray(tagsData)
          ? tagsData
            .filter(t => t.isActive === true || t.isActive === 1)
            .map((t) => ({
              id: t.tagId,
              name: t.tagName,
            }))
          : [];


        // ===== NORMALIZE CATEGORIES =====
        // داخل useEffect الخاص بـ Categories
        // داخل useEffect الخاص بـ Categories
        const normalizedCategories = Array.isArray(categoriesData)
          ? categoriesData
            .filter(c => c.isActive === true || c.isActive === 1)
            .map((c) => ({
              id: c.categoryId,
              name: c.categoryName,
              // تأكد من الـ Key القادم من الـ API (قد يكون SubCategories أو subCategories)
              subCategories: Array.isArray(c.subCategories || c.SubCategories)
                ? (c.subCategories || c.SubCategories).map((sc) => ({
                  id: sc.subCategoryId || sc.id, // جرب الاحتمالين
                  name: sc.subCategoryName || sc.name,
                }))
                : [],
            }))
          : [];
        setCategories(normalizedCategories);

        setTags(normalizedTags);
      })
      .catch((err) => {
        console.error("Fetch Tags/Categories Error:", err);
        setTags([]);
        setCategories([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [API_BASE, tenantId]);

  const [rawItems, setRawItems] = useState([]);

  // ==== SEARCH ====
  const [query, setQuery] = useState("");
  const filteredProducts = useMemo(() => {
    // If we let ResponsiveTable handle search, we can just return products here
    // But since the user complained search is not working, I'll make sure it's linked
    return products;
  }, [products]);


  // ==== MODAL ====
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [current, setCurrent] = useState(null);
  const [zoomImage, setZoomImage] = useState(null);
  const [form, setForm] = useState({
    name: "",
    itemCode: "",
    tagsId: 0,
    categoryId: 0,
    subCategoryId: 0,
    description: "",
    isActive: true,
    isSingleProduct: false, // New field
    variants: [
      {
        variantName: "",
        price: "",
        costPrice: "",
        discount: { type: 0, value: 0 },
        lowStockValue: 0,
        colorGroups: [
          {
            color: { name: "#000000" },
            image: { file: null, existingUrl: null, isChanged: false },
            sizes: [{ size: "", quantity: "" }]
          }
        ]
      }
    ],
    mainImage: { file: null, existingUrl: null, isChanged: false },
    discountValue: 0,
    discountType: 0, // 0 for Fixed, 1 for Percentage
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [expandedVariant, setExpandedVariant] = useState(0); // Track expanded variant index

  const handleOpenMovement = (targetItemId) => {
    navigate(`/admin-store/products/movement/${targetItemId}`);
  };


  const getCategoryId = (item) =>
    item?.category?.id ??
    item?.categoryId ??
    0;

  const mapApiItemToProduct = (item, stockData = null) => {
    const variantsFromApi = Array.isArray(item?.variants) ? item.variants : [];

    // Ensure itemStockData is always an array
    let itemStockData = [];
    if (Array.isArray(stockData)) {
      itemStockData = stockData;
    } else if (stockData && typeof stockData === 'object' && stockData !== null) {
      itemStockData = [stockData];
    } else {
      const fromMap = stockMap.get(item.itemId);
      itemStockData = Array.isArray(fromMap) ? fromMap : (fromMap ? [fromMap] : []);
    }

    // Get stock for each variant by variantId
    const processedVariants = variantsFromApi.map(v => {
      // Find matching stock entry by variantId or SerialNumber if applicable
      const vStock = (Array.isArray(itemStockData) ? itemStockData : [])
        .find(s => (s.variantId || s.VariantId || s.id || s.Id) === v.variantId) || {};
      return normalizeVariant(v, vStock);
    });

    // Find images and sort them
    const images = [...processedVariants]
      .sort((a, b) => (Number(b.stock?.quantity) || 0) - (Number(a.stock?.quantity) || 0))
      .map(v => v.imageUrl || v.image?.existingUrl || null)
      .filter((img, idx, self) => img && self.indexOf(img) === idx);

    // Total quantity should be sum of all sizes in all variants if present in GetItems
    let totalQty = variantsFromApi.reduce((sum, v) => {
      if (v.sizes && Array.isArray(v.sizes)) {
        return sum + v.sizes.reduce((sSum, sz) => sSum + (sz.stockQty || 0), 0);
      }
      return sum + (v.stockQty || v.quantity || 0);
    }, 0);

    // Fallback to stockMap if totalQty is still 0 (only if stockMap has data)
    if (totalQty === 0 && itemStockData.length > 0) {
      totalQty = itemStockData.reduce((sum, s) => sum + (Number(s.availableStock ?? s.totalStock ?? s.quantity ?? 0)), 0);
    }

    const prices = processedVariants
      .map(v => Number(v.price))
      .filter(n => Number.isFinite(n));

    const costPrices = processedVariants
      .map(v => Number(v.costPrice))
      .filter(n => Number.isFinite(n));

    const minPrice = prices.length ? Math.min(...prices) : 0;
    const maxPrice = prices.length ? Math.max(...prices) : 0;

    const minCost = costPrices.length ? Math.min(...costPrices) : 0;
    const maxCost = costPrices.length ? Math.max(...costPrices) : 0;

    const catId = getCategoryId(item);
    const categoryName =
      categories.find(c => c.id === catId)?.name ||
      item?.category?.name ||
      "—";

    return {
      itemId: item.itemId,
      name: item.itemName || "",
      itemCode: item.itemCode || "",
      description: item.description || "",
      isActive: item.isActive ?? true,
      categoryId: catId,
      category: categoryName,
      subCategoryId:
        item.subCategory?.subCategoryId ??
        item.subCategory?.id ??
        0,
      subCategory:
        item.subCategory?.subCategoryName ??
        item.subCategory?.name ??
        "—",
      tagsId: item.tags?.tagId ?? item.tags?.id ?? 0,
      type: item.tags?.tagName ?? item.tags?.name ?? "—",
      orders: item.ordersCount ?? 0,
      quantity: totalQty,
      priceMin: minPrice,
      priceMax: maxPrice,
      costPriceMin: minCost,
      costPriceMax: maxCost,
      stockStatus: item.stockStatus || (totalQty > 0 ? "AVAILABLE" : "OUT_OF_STOCK"),
      variants: processedVariants,
      variantCount: processedVariants.length,
      variantImages: images,
      mainImage: item.mainImageUrl || item.mainImage,
      discountValue: item.discountValue || 0,
      discountType: item.discountType || 0,
    };
  };

  const urlToFile = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      // نحدد النوع بناءً على البلوب أو نفترضه image/jpeg
      return new File([blob], fileName, { type: blob.type || "image/jpeg" });
    } catch (error) {
      console.error("Failed to convert URL to File:", error);
      return null;
    }
  };

  const buildImageUrl = (imagePath) => {
    if (!imagePath || imagePath === "" || imagePath === "null") {
      return "https://placehold.co/120x120/e0e0e0/666?text=Product";
    }

    // For local blob previews (newly uploaded images)
    if (imagePath.startsWith("blob:")) return imagePath;

    // إذا كان الرابط كامل يبدأ بـ http لا نغيره
    if (imagePath.startsWith("http")) return imagePath;

    // تنظيف المسار من أي سلاش زائد في البداية
    let cleanPath = imagePath.startsWith("/") ? imagePath.substring(1) : imagePath;

    // إذا كان المسار يحتوي أصلاً على كلمة uploads، ندمجه مباشرة مع الـ BaseURL
    if (cleanPath.startsWith("uploads/")) {
      return `${baseImageUrl}/${cleanPath}`;
    }

    // الحالة الاحتياطية
    return `${baseImageUrl}/uploads/${tenantId}/items/${cleanPath.split('/').pop()}`;
  };

  const getProductDisplayImage = (product) =>
    product?.mainImage || product?.mainImageUrl || (product?.variantImages || [])[0] || null;
  // داخل الـ .then الخاص بـ GetItems

  const normalizeVariant = (v, vStock = {}) => {
    const vStockData = vStock || {};

    // استخراج الألوان بشكل دقيق من الـ API
    // لاحظ هنا نأخذ hexCode و name كما يظهر في الـ Response
    const colorObj = v.color || vStockData.color || null;

    const colorInfo = colorObj ? {
      id: colorObj.id || 0,
      name: colorObj.hexCode || "#ffffff", // Internal 'name' is Hex for color picker
      colorName: colorObj.name || ""     // Internal 'colorName' is Text Name
    } : null;

    // حساب الكمية من الـ sizes
    let quantity = 0;
    let sizeName = "";
    let lowStockVal = 0;


    if (v.sizes && Array.isArray(v.sizes) && v.sizes.length > 0) {
      quantity = v.sizes.reduce((sum, s) => sum + (s.stockQty || s.StockQty || s.quantity || s.Quantity || 0), 0);
      sizeName = v.sizes[0].size || v.sizes[0].Size || "";
      lowStockVal = v.sizes[0].lowStockValue || v.sizes[0].LowStockValue || 0;
    } else {
      quantity = v.stockQty || v.StockQty || v.quantity || v.Quantity || 0;
      lowStockVal = v.lowStockValue || v.LowStockValue || 0;
    }

    const normalizedSizes = (v.sizes || []).map(s => ({
      variantId: s.variantId || s.VariantId || 0,
      size: s.size || s.Size || "",
      quantity: s.stockQty || s.StockQty || s.quantity || s.Quantity || 0,
      lowStockValue: s.lowStockValue || s.LowStockValue || 0
    }));

    return {
      variantId: v.variantId || v.VariantId,
      variantName: v.variantName || v.VariantName || "",
      color: colorInfo,
      memory: sizeName ? { id: 0, name: sizeName } : (v.memory || null),
      price: v.price || v.Price || 0,
      costPrice: v.costPrice || v.CostPrice || 0,
      stock: {
        quantity: quantity,
        status: v.stockStatus || (quantity > 0 ? "AVAILABLE" : "OUT_OF_STOCK"),
        lowStockValue: lowStockVal,
      },
      sizes: normalizedSizes.length > 0 ? normalizedSizes : [],
      imageUrl: v.imageUrl || null,
    };
  };
  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setForm((f) => ({
        ...f,
        mainImage: { file, existingUrl: url, isChanged: true },
      }));
      // مسح خطأ التحقق إذا وجد
      if (fieldErrors.mainImage) {
        setFieldErrors((prev) => ({ ...prev, mainImage: false }));
      }
    }
  };
  const updateColorHex = (vidx, cgIdx, hex) => {
    setForm((f) => {
      const newVariants = [...f.variants];
      const newV = { ...newVariants[vidx] };
      newV.colorGroups = [...newV.colorGroups];
      newV.colorGroups[cgIdx] = {
        ...newV.colorGroups[cgIdx],
        color: { ...newV.colorGroups[cgIdx].color, name: hex },
      };
      newVariants[vidx] = newV;
      return { ...f, variants: newVariants };
    });
  }; const updateColorName = (vidx, cgIdx, name) => {
    setForm((f) => {
      const newVariants = [...f.variants];
      const newV = { ...newVariants[vidx] };
      newV.colorGroups = [...newV.colorGroups];
      newV.colorGroups[cgIdx] = {
        ...newV.colorGroups[cgIdx],
        color: { ...newV.colorGroups[cgIdx].color, colorName: name },
      };
      newVariants[vidx] = newV;
      return { ...f, variants: newVariants };
    });
  };

  useEffect(() => {
    const token = localStorage.getItem("authToken");

    if (!tenantId || tenantId <= 0) {
      setLoading(false);
      return;
    }

    setLoading(true);

    Promise.all([
      // 1. Get Items
      fetch(`${API_BASE}/GetItems?tenantId=${tenantId}&itemId=-1`, {
        method: "GET",
        headers: {
          Accept: "*/*",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).then(async (res) => {
        if (!res.ok) {
          console.error(`GetItems failed with status: ${res.status} for tenant: ${tenantId}`);
          throw new Error(`Failed to fetch products (Status: ${res.status})`);
        }
        const data = await res.json();
        return Array.isArray(data) ? data : [];
      }),

      // 2. Get Stock (for correct quantities)
      fetch(`${API_BASE}/ItemsStock?tenantId=${tenantId}&itemId=-1`, {
        method: "GET",
        headers: {
          Accept: "*/*",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).then(async (res) => {
        if (!res.ok) return [];
        const data = await res.json();
        return Array.isArray(data) ? data : [];
      })
    ])
      .then(([itemsData, stockData]) => {
        // تحضير بيانات المخزون في Map
        const sMap = new Map();
        stockData.forEach(s => {
          const id = s.itemId || s.ItemId;
          if (!sMap.has(id)) sMap.set(id, []);
          sMap.get(id).push(s);
        });

        setStockMap(sMap);
        setRawItems(itemsData);

        // ✅ الحل هنا: تصفية المنتجات النشطة فقط وعمل الـ Map داخل الـ then
        const activeItems = itemsData.filter(item => item.isActive !== false);
        setProducts(activeItems.map(item => mapApiItemToProduct(item, sMap.get(item.itemId))));
      })
      .catch((err) => {
        console.error("Fetch products/stock error:", err);
        setRawItems([]);
        setProducts([]);
      })
      .finally(() => {
        setLoading(false);
      });

  }, [API_BASE, tenantId]);

  useEffect(() => {
    if (!rawItems.length) return;
    // Re-map if categories change, using existing stockMap
    setProducts(rawItems.map(item => mapApiItemToProduct(item, stockMap.get(item.itemId))));
  }, [rawItems, categories, stockMap]);

  // Restore form state after navigating back from Brand/Category pages
  useEffect(() => {
    const saved = sessionStorage.getItem("all_products_form_state");
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setForm(data.form);
        setModalMode(data.modalMode);
        setCurrent(data.current);
        setExpandedVariant(data.expandedVariant);
        setModalOpen(data.modalOpen);
        sessionStorage.removeItem("all_products_form_state");
      } catch (e) {
        console.error("Error restoring form state:", e);
      }
    }
  }, []);



  useEffect(() => {
    if (modalOpen || zoomImage) {
      document.body.style.overflow = 'hidden';
      document.body.style.pointerEvents = 'auto';
    } else {
      document.body.style.overflow = '';
      document.body.style.pointerEvents = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.pointerEvents = '';
    };
  }, [modalOpen, zoomImage]);

  // Page-level permission guard (after all hooks)
  if (!perms.viewProducts) {
    return <NoAccess />;
  }

  const totalProducts = products.length;
  const availableProducts = products.filter((p) => p.quantity > 0).length;

  const cards = [
    {
      title: isArabic ? "عدد كل المنتجات" : "Total Products",
      value: totalProducts,
    },
    {
      title: isArabic ? "المنتجات المتوفّرة" : "Available Products",
      value: availableProducts,
    },
  ];

  const openModal = async (mode, item = null) => {
    setModalMode(mode);
    setCurrent(item);
    setExpandedVariant(0); // Open first variant
    setFieldErrors({});

    if (mode === "create") {
      setForm({
        name: "",
        itemCode: "",
        tagsId: 0,
        categoryId: 0,
        subCategoryId: 0,
        description: "",
        isActive: true,
        isSingleProduct: false,
        variants: [
          {
            variantName: "",
            price: "",
            costPrice: "",
            discount: { type: 0, value: 0 },
            lowStockValue: 0,
            colorGroups: [
              {
                color: { name: "#000000", colorName: "" },
                image: { file: null, existingUrl: null, isChanged: false },
                sizes: [{ size: "", quantity: "", lowStockValue: 0 }]
              }
            ]
          }
        ],
        mainImage: { file: null, existingUrl: null, isChanged: false },
        discountValue: 0,
        discountType: 0,
      });
    } else if (item) {
      const data = mapApiItemToProduct(item);

      if (mode === "view") {
        setCurrent(data);
      } else {
        // Edit mode - Populate Form
        // Group variants by base details -> then by Color -> then list of sizes
        const grouped = [];
        data.variants.forEach(v => {
          // Removed lowStockValue from key to allow grouping differing stock thresholds
          const baseKey = `${v.variantName}-${v.price}-${v.costPrice || ''}-${v.discount?.type}-${v.discount?.value}`;
          let existingBase = grouped.find(g => g._baseKey === baseKey);

          if (!existingBase) {
            existingBase = {
              _baseKey: baseKey,
              variantName: v.variantName,
              price: v.price,
              costPrice: v.costPrice || "",
              lowStockValue: 0, // Default for group, specific values are in sizes
              colorGroups: []
            };
            grouped.push(existingBase);
          }

          const colorName = v.color?.name || "#000000";
          let existingColor = existingBase.colorGroups.find(cg => cg.color.name === colorName);

          if (!existingColor) {
            existingColor = {
              color: { name: colorName, colorName: v.color?.colorName || "" },
              image: { file: null, existingUrl: v.imageUrl, isChanged: false },
              discount: { type: v.discount?.type || 0, value: v.discount?.value || 0 },
              sizes: []
            };
            existingBase.colorGroups.push(existingColor);
          }
          if (v.sizes && v.sizes.length > 0) {
            v.sizes.forEach(sz => {
              existingColor.sizes.push({
                variantId: sz.variantId,
                size: sz.size,
                quantity: sz.quantity,
                lowStockValue: sz.lowStockValue
              });
            });
          } else {
            existingColor.sizes.push({
              variantId: v.variantId,
              size: v.memory?.name || v.size?.name || "", // Check both for compatibility
              quantity: v.stock?.quantity || v.quantity || 0,
              lowStockValue: v.stock?.lowStockValue || 0
            });
          }
        });

        const finalVariants = grouped.map(g => {
          return {
            ...g
          };
        });

        setForm({
          name: data.name,
          itemCode: data.itemCode,
          tagsId: data.tagsId,
          categoryId: data.categoryId,
          subCategoryId: data.subCategoryId,
          description: data.description,
          isActive: data.isActive,
          isSingleProduct: data.categoryId === 0 && data.tagsId === 0,
          variants: finalVariants.length > 0 ? finalVariants : [
            {
              variantName: "",
              price: "",
              costPrice: "",
              discount: { type: 0, value: 0 },
              lowStockValue: 0,
              colorGroups: [
                {
                  color: { name: "#000000" },
                  image: { file: null, existingUrl: null, isChanged: false },
                  sizes: [{ size: "", quantity: "", lowStockValue: 0 }]
                }
              ]
            }
          ],
          mainImage: { file: null, existingUrl: data.mainImage, isChanged: false },
          discountValue: data.discountValue || 0,
          discountType: data.discountType || 0
        });
      }
    }
    setModalOpen(true);
  };


  const closeModal = () => {
    setModalOpen(false);
  };



  const buildTenantItemFormData = async ({ mode, current }) => {
    const fd = new FormData();
    const itemId = mode === "edit" ? (current?.itemId || current?.ItemId || current?.id) : "";

    const generatedItemCode = itemId ? `${form.name.trim()}-${itemId}` : form.name.trim();

    // 1. البيانات الأساسية
    fd.append("TenantId", tenantId);
    fd.append("ItemName", form.name.trim());
    fd.append("ItemCode", generatedItemCode); fd.append("TagsId", form.tagsId || 0);
    fd.append("CategoryId", form.categoryId || 0);
    fd.append("SubCategoryId", form.subCategoryId || 0);
    fd.append("Description", form.description || "");
    fd.append("IsActive", form.isActive);
    fd.append("DiscountValue", Number(form.discountValue || 0));
    fd.append("DiscountType", Number(form.discountType || 0));

    if (mode === "edit" && current) {
      const itemId = current.itemId || current.ItemId || current.id;
      fd.append("ItemId", itemId);
    }

    // 2. معالجة الصورة الرئيسية (Main Image)
    if (form.mainImage?.file && (form.mainImage.isChanged || mode === "create")) {
      fd.append("MainImage", form.mainImage.file);
    } else if (mode === "edit" && form.mainImage?.existingUrl) {
      // إذا كنت تريد إرسال الصورة الرئيسية كملف أيضاً لتجنب حذفها:
      const mainFile = await urlToFile(buildImageUrl(form.mainImage.existingUrl), "main_image.jpg");
      if (mainFile) fd.append("MainImage", mainFile);
    }

    let imageCounter = 0;
    const variantImageFiles = [];
    const mappedVariants = [];

    // 3. تحويل المتغيرات (Variants) مع معالجة الصور كملفات دايماً
    for (let vidx = 0; vidx < form.variants.length; vidx++) {
      const gv = form.variants[vidx];
      const price = Number(gv.price || 0);
      const costPrice = Number(gv.costPrice || 0);

      for (let cgIdx = 0; cgIdx < gv.colorGroups.length; cgIdx++) {
        const cg = gv.colorGroups[cgIdx];
        let currentImageKey = null;

        // منطق الصور: تحويل الرابط لملف إذا لم تكن صورة جديدة
        if (cg.image?.file && cg.image.isChanged) {
          // حالة صورة جديدة مرفوعة
          currentImageKey = imageCounter.toString();
          variantImageFiles.push({
            file: cg.image.file,
            index: currentImageKey
          });
          imageCounter++;
        } else if (cg.image?.existingUrl) {
          // حالة صورة قديمة: نحولها لملف ونبعثها كأنها مرفوعة الآن
          currentImageKey = imageCounter.toString();
          const existingFile = await urlToFile(buildImageUrl(cg.image.existingUrl), `existing_v_${imageCounter}.jpg`);

          if (existingFile) {
            variantImageFiles.push({
              file: existingFile,
              index: currentImageKey
            });
            imageCounter++;
          }
        } else {
          // إذا لم يتم توفير صورة فرعية، نستخدم الصورة الرئيسية كبديل
          if (form.mainImage?.file) {
            currentImageKey = imageCounter.toString();
            variantImageFiles.push({
              file: form.mainImage.file,
              index: currentImageKey
            });
            imageCounter++;
          } else if (form.mainImage?.existingUrl) {
            currentImageKey = imageCounter.toString();
            const existingFile = await urlToFile(buildImageUrl(form.mainImage.existingUrl), `fallback_v_${imageCounter}.jpg`);
            if (existingFile) {
              variantImageFiles.push({
                file: existingFile,
                index: currentImageKey
              });
              imageCounter++;
            }
          }
        }

        // إنشاء كائن المتغير للـ JSON
        mappedVariants.push({
          VariantId: cg.sizes[0]?.variantId || 0,
          VariantName: gv.variantName,
          color: {
            Id: cg.color?.id || 0,
            Name: cg.color?.colorName || cg.color?.name || "Color",
            HexCode: cg.color?.name || "#ffffff"
          },
          Price: price,
          CostPrice: costPrice,
          Sizes: cg.sizes.map(sz => ({
            Id: sz.variantId || 0,
            Size: sz.size || "S",
            StockQty: Number(sz.quantity || 0),
            LowStockType: 1,
            LowStockValue: Number(sz.lowStockValue || 0),
            Status: "AVAILABLE"
          })),
          ImageKey: currentImageKey, // دائماً يشير إلى Index الملف المرفوع
          ImageUrl: null // نضعه null لأننا أرسلنا الملف فعلياً في VariantImages
        });
      }
    }

    // 4. إرسال المصفوفة كـ JSON
    fd.append("Variants", JSON.stringify(mappedVariants));

    // 5. إرفاق جميع الملفات (الجديدة والقديمة المحولة)
    variantImageFiles.forEach((imgObj) => {
      fd.append(`VariantImages[${imgObj.index}]`, imgObj.file);
    });

    return fd;
  };

  const handleGoToBrands = () => {
    sessionStorage.setItem("all_products_form_state", JSON.stringify({
      form,
      modalMode,
      current,
      expandedVariant,
      modalOpen: true
    }));
    navigate("/admin-store/categories", { state: { openAdd: true, returnTo: "/admin-store/products" } });
  };

  const handleGoToCategories = () => {
    sessionStorage.setItem("all_products_form_state", JSON.stringify({
      form,
      modalMode,
      current,
      expandedVariant,
      modalOpen: true
    }));
    navigate("/admin-store/divisions", { state: { openAdd: true, returnTo: "/admin-store/products" } });
  };


  const handleSave = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!form.name?.trim()) errors.name = true;

    if (!form.mainImage.file && !form.mainImage.existingUrl) {
      errors.mainImage = true;
    }

    if (!form.isSingleProduct) {
      if (!form.tagsId || form.tagsId === 0) errors.tagsId = true;
      if (!form.categoryId || form.categoryId === 0) errors.categoryId = true;
    }

    if (!form.variants || form.variants.length === 0) {
      Swal.fire({
        icon: "error",
        title: isArabic ? "المتغيرات مطلوبة" : "Variants required",
        confirmButtonColor: colors.mainColor,
      });
      return;
    }

    // Validate Variants Basic Fields
    form.variants.forEach((v, vidx) => {
      if (!v.variantName) errors[`variants[${vidx}].variantName`] = true;
      if (!v.price) errors[`variants[${vidx}].price`] = true;
      if (!v.costPrice) errors[`variants[${vidx}].costPrice`] = true;
      if (v.lowStockValue === "" || v.lowStockValue === null || v.lowStockValue === undefined) errors[`variants[${vidx}].lowStockValue`] = true;

      v.colorGroups.forEach((cg, cgIdx) => {
        cg.sizes.forEach((sz, szIdx) => {
          if (!sz.size) {
            errors[`variants[${vidx}].colorGroups[${cgIdx}].sizes[${szIdx}].size`] = true;
          }
          if (sz.quantity === "" || sz.quantity === null || sz.quantity === undefined) {
            errors[`variants[${vidx}].colorGroups[${cgIdx}].sizes[${szIdx}].quantity`] = true;
          }
        });
      });
    });

    // --- إضافة: التحقق من أن السعر بعد الخصم ليس صفراً أو أقل ---
    let hasInvalidPrice = false;
    let hasLossPrice = false;
    let problematicVariant = "";

    form.variants.forEach((v) => {
      const price = parseFloat(v.price) || 0;
      const cost = parseFloat(v.costPrice) || 0;
      const discVal = parseFloat(form.discountValue) || 0;

      // حساب السعر النهائي باستخدام الدالة الموجودة مسبقاً في الكود
      const finalPrice = calculatePriceAfterDiscount(price, discVal, form.discountType);

      if (finalPrice <= 0 && price > 0) {
        hasInvalidPrice = true;
      }

      // الشرط الجديد: إذا كان السعر النهائي أقل من التكلفة
      if (finalPrice < cost) {
        hasLossPrice = true;
        problematicVariant = v.variantName || (isArabic ? "أحد المتغيرات" : "one of the variants");
      }
    });

    if (hasInvalidPrice) {
      Swal.fire({
        icon: "error",
        title: isArabic ? "خطأ في السعر النهائي" : "Invalid Final Price",
        text: isArabic
          ? "قيمة الخصم تجعل سعر المنتج صفراً أو أقل!"
          : "Discount value makes the price zero or less!",
        confirmButtonColor: colors.mainColor,
      });
      return;
    }

    // تنبيه الخسارة
    if (hasLossPrice) {
      Swal.fire({
        icon: "error",
        title: isArabic ? "تنبيه: خسارة محتملة" : "Warning: Potential Loss",
        text: isArabic
          ? `سعر البيع بعد الخصم للمتغير (${problematicVariant}) أقل من سعر التكلفة! لا يمكن البيع بخسارة.`
          : `Final price for (${problematicVariant}) is lower than the cost price! You cannot sell at a loss.`,
        confirmButtonColor: colors.mainColor,
      });
      return;
    }
    // -------------------------------------------------------

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      Swal.fire({
        icon: "error",
        title: isArabic ? "خطأ في البيانات" : "Validation Error",
        text: isArabic ? "يرجى ملء جميع الحقول المطلوبة (باللون الأحمر)" : "Please fill all required fields (highlighted in red)",
        confirmButtonColor: colors.mainColor,
      });
      return;
    }

    // For edit mode, use UpdateItems API
    if (modalMode === "edit" && current) {
      try {
        const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
        const token = localStorage.getItem("authToken");

        const fd = await buildTenantItemFormData({
          mode: "edit",
          current,
        });

        const res = await fetch(
          `${API_BASE}/UpdateItems/UpdateTenantItem`,
          {
            method: "POST",
            headers: {
              Authorization: token ? `Bearer ${token}` : "",
            },
            body: fd,
          }
        );

        let data = {};
        const ct = res.headers.get("content-type");
        if (ct && ct.includes("application/json")) {
          data = await res.json();
        } else {
          const t = await res.text();
          try {
            data = t ? JSON.parse(t) : {};
          } catch {
            data = { message: t };
          }
        }

        if (!res.ok) {
          const apiErrors = {};
          if (data?.errors) {
            Object.keys(data.errors).forEach(key => {
              if (key.toLowerCase().includes("name")) apiErrors.name = true;
              if (key.toLowerCase().includes("category")) apiErrors.categoryId = true;
              if (key.toLowerCase().includes("tags")) apiErrors.tagsId = true;
            });
            setFieldErrors(apiErrors);
          }

          let errorMessage = data?.message || data?.error || data?.Message || (isArabic ? "فشل تحديث المنتج ❌" : "Failed to update product ❌");
          Swal.fire({
            icon: "error",
            title: isArabic ? "فشل التحديث ❌" : "Update Failed ❌",
            text: errorMessage,
            confirmButtonColor: colors.mainColor,
          });
          return;
        }

        Swal.fire({
          icon: "success",
          title: isArabic ? "تم التعديل! ✅" : "Updated! ✅",
          confirmButtonColor: colors.mainColor,
          timer: 2000,
          showConfirmButton: false,
        });

        closeModal();

        setLoading(true);
        const itemId = getItemId(current);
        const refetch = await fetch(
          `${API_BASE}/GetItems?tenantId=${tenantId}&itemId=${itemId}`,
          { headers: { Authorization: token ? `Bearer ${token}` : "" } }
        );

        if (refetch.ok) {
          const [freshItem] = await refetch.json();
          setRawItems(prev => prev.map(it => it.itemId === itemId ? freshItem : it));
          setProducts(prev => prev.map(p => p.itemId === itemId ? mapApiItemToProduct(freshItem) : p));
        }
        setLoading(false);

      } catch (err) {
        Swal.fire({
          icon: "error",
          title: isArabic ? "فشل التحديث ❌" : "Update Failed ❌",
          text: err.message,
          confirmButtonColor: colors.mainColor,
        });
      }
      return;
    }

    if (modalMode === "create") {
      try {
        const token = localStorage.getItem("authToken");
        const fd = await buildTenantItemFormData({ mode: "create" });

        const res = await fetch(
          `${API_BASE}/TenantsItems/AddTenantItem`,
          {
            method: "POST",
            headers: {
              Authorization: token ? `Bearer ${token}` : "",
            },
            body: fd,
          }
        );

        let data = {};
        const ct = res.headers.get("content-type");
        if (ct && ct.includes("application/json")) {
          data = await res.json();
        } else {
          const t = await res.text();
          data = t ? JSON.parse(t) : {};
        }

        if (!res.ok) throw new Error(data?.message || "Add failed");

        Swal.fire({
          icon: "success",
          title: isArabic ? "تمت الإضافة ✅" : "Added ✅",
          timer: 1500,
          showConfirmButton: false,
        });

        closeModal();

        setLoading(true);
        const r = await fetch(`${API_BASE}/GetItems?tenantId=${tenantId}&itemId=-1`, {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        });
        if (r.ok) {
          const list = await r.json();
          setRawItems(list);
          setProducts(list.map(mapApiItemToProduct));
        }
        setLoading(false);
      } catch (e) {
        Swal.fire({ icon: "error", title: "Error", text: e.message });
      }
      return;
    }
  };

  const handleDelete = async (item) => {
    const itemId = getItemId(item);
    const tenantId = Number(localStorage.getItem("tenantId")) || -1;
    const token = localStorage.getItem("authToken");

    // 🔴 Validation قبل أي شيء
    if (!itemId || itemId <= 0 || tenantId <= 0) {
      Swal.fire({
        icon: "error",
        title: isArabic ? "خطأ في البيانات" : "Invalid Data",
        text: isArabic
          ? "رقم المنتج أو التاجر غير صحيح"
          : "Invalid itemId or tenantId",
        confirmButtonColor: colors.mainColor,
      });
      return;
    }

    const confirm = await Swal.fire({
      title: isArabic ? "هل أنت متأكد؟" : "Are you sure?",
      text: isArabic
        ? `سيتم حذف المنتج "${item.name}" نهائيًا`
        : `Product "${item.name}" will be permanently deleted`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#7c3aed", // Fixed Purple to ensure visibility
      cancelButtonColor: "#ef4444", // Red for Cancel
      confirmButtonText: isArabic ? "نعم، احذف" : "Yes, delete",
      cancelButtonText: isArabic ? "إلغاء" : "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await fetch(
        `${API_BASE}/DeleteItem?tenantId=${tenantId}&itemId=${itemId}`,
        {
          method: "DELETE",
          headers: {
            Accept: "*/*",
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );

      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = { message: text };
      }

      if (!res.ok) {
        throw new Error(
          data?.message ||
          (isArabic ? "فشل حذف المنتج" : "Failed to delete product")
        );
      }

      // ✅ تحديث الـ state بشكل صحيح
      setProducts((prev) =>
        prev.filter((p) => getItemId(p) !== itemId)
      );

      Swal.fire({
        icon: "success",
        title: isArabic ? "تم الحذف ✅" : "Deleted ✅",
        confirmButtonColor: colors.mainColor,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: isArabic ? "خطأ" : "Error",
        text: err.message,
        confirmButtonColor: colors.mainColor,
      });
    }
  };


  // ==== UI ====
  if (loading) {
    return (
      <PageContainer title={isArabic ? "كل المنتجات" : "All Products"}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
            <p className="text-gray-400">{isArabic ? "جاري التحميل..." : "Loading..."}</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer title={isArabic ? "كل المنتجات" : "All Products"}>
      {/* ===== العنوان + الإحصاءات ===== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {cards.map((c, i) => (
          <UnifiedCard
            key={i}
            icon={FiBox}
            title={c.title}
            value={c.value}
            valueColor={colors.gold}
            statCard={true}
          />
        ))}
      </div>

      {/* ===== البحث + زر الإضافة ===== */}


      {/* ===== الجدول ===== */}
      <div className="w-full">
        <ResponsiveTable
          columns={[
            { header: isArabic ? "صورة" : "Image", align: "center" },
            { header: isArabic ? "الاسم" : "Name", sortKey: "name", align: "center" },
            { header: isArabic ? "البراند" : "Brand", sortKey: "type", align: "center" }, // Renamed from Type to Brand
            { header: isArabic ? "القسم" : "Category", sortKey: "category", align: "center" },
            { header: isArabic ? "السعر" : "Price", align: "center" },
            { header: isArabic ? "التكلفة" : "Cost", align: "center" },
            { header: isArabic ? "الكمية" : "Qty", align: "center" },
            { header: isArabic ? "عدد الطلبات" : "Orders", align: "center" },
            { header: isArabic ? "تحكم" : "Actions", align: "center" },
          ]}

          data={products}
          searchable={true}
          searchFields={["name", "category", "type", "subCategory"]}
          sortable={true}
          headerActions={
            perms.createProduct && (
              <button
                onClick={() => openModal("create")}
                className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-white font-medium active:scale-[1.02] transition-transform w-full md:w-auto"
                style={{
                  backgroundColor: colors.mainColor,
                  boxShadow: "0 4px 12px rgba(116,0,191,0.25)",
                }}
              >
                <FiPlus />
                {isArabic ? "منتج جديد" : "Add Product"}
              </button>
            )
          }
          filterOptions={{
            field: "category",
            options: [
              { value: "", label: isArabic ? "الكل" : "All" },
              ...Array.from(new Set(products.map((p) => p.category).filter(Boolean))).map((cat) => ({
                value: cat,
                label: cat,
              })),
            ],
          }}
          renderRow={(p) => (
            <>
              <td className="py-3 px-4 text-center">
                <div className="flex items-center justify-center">
                  <img
                    src={buildImageUrl(getProductDisplayImage(p))}
                    className="w-10 h-10 object-cover rounded cursor-pointer"
                    onClick={() => setZoomImage(buildImageUrl(getProductDisplayImage(p)))}
                  />
                </div>
              </td>
              <td className="py-3 px-4 text-center" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                {(() => {
                  const name = p.name || "—";
                  if (name === "—") return <span className="font-medium text-sm sm:text-base whitespace-nowrap">—</span>;
                  const words = name.split(" ");
                  if (words.length <= 2) return <span className="font-medium text-sm sm:text-base whitespace-nowrap">{name}</span>;
                  return (
                    <div className="flex flex-col items-center justify-center">
                      <span className="font-medium text-sm sm:text-base whitespace-nowrap">{words.slice(0, 2).join(" ")}</span>
                      <span className="text-xs mt-1 whitespace-normal break-words max-w-[180px]" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>{words.slice(2).join(" ")}</span>
                    </div>
                  );
                })()}
              </td>
              <td className="py-3 px-4 text-center" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                <span className="text-sm sm:text-base whitespace-nowrap">{renderText(p.type)}


                </span>
              </td>
              <td className="py-3 px-4 text-center" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                <span className="text-sm sm:text-base whitespace-nowrap">{p.category || "—"}</span>
              </td>
              <td className="py-3 px-4 text-center">
                <div className="flex flex-col items-center justify-center whitespace-nowrap">
                  <span className="font-semibold text-sm sm:text-base" style={{ color: colors.gold }}>
                    {p.priceMin === p.priceMax
                      ? `${p.priceMin}`
                      : `${p.priceMin} - ${p.priceMax}`
                    } {isArabic ? "د.ع" : "IQD"}
                  </span>
                </div>
              </td>
              <td className="py-3 px-4 text-center">
                <div className="flex flex-col items-center justify-center whitespace-nowrap">
                  <span className="font-semibold text-sm sm:text-base" style={{ color: colors.red }}>
                    {p.costPriceMin === p.costPriceMax
                      ? `${p.costPriceMin}`
                      : `${p.costPriceMin} - ${p.costPriceMax}`
                    } {isArabic ? "د.ع" : "IQD"}
                  </span>
                </div>
              </td>
              <td className="py-3 px-4 text-center" style={{ color: (p.quantity === 0 || p.stockStatus === "LOW_STOCK" || p.stockStatus === "OUT_OF_STOCK") ? colors.red : (isDark ? "#f1f5f9" : "#111827") }}>
                <span className="text-sm sm:text-base whitespace-nowrap">{p.quantity || 0}</span>
              </td>
              <td className="py-3 px-4 text-center" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                <span className="text-sm sm:text-base whitespace-nowrap">{p.orders || 0}</span>
              </td>
              <td className="py-3 px-4 text-center">
                <div className="flex justify-center items-center gap-2 flex-nowrap whitespace-nowrap">
                  <button
                    onClick={() => {
                      const fullItem = rawItems.find(r => r.itemId === p.itemId);
                      if (!fullItem) return;
                      openModal("view", fullItem);

                    }}


                    className="p-2 sm:p-2.5 rounded-lg text-white transition-all active:scale-105 shadow-sm flex-shrink-0"
                    style={{ background: colors.gold, color: isDark ? "#1e293b" : "#ffffff" }}
                    title={isArabic ? "عرض" : "View"}
                  >
                    <FiEye size={16} />
                  </button>
                  {perms.editProduct && (
                    <button
                      onClick={async () => {
                        const token = localStorage.getItem("authToken");
                        const res = await fetch(
                          `${API_BASE}/GetItems?tenantId=${tenantId}&itemId=${p.itemId}`,
                          { headers: { Authorization: token ? `Bearer ${token}` : "" } }
                        );

                        if (!res.ok) return;

                        const [fullItem] = await res.json();
                        openModal("edit", fullItem);
                      }}


                      className="p-2 sm:p-2.5 rounded-lg text-white transition-all active:scale-105 shadow-sm flex-shrink-0"
                      style={{ background: colors.mainColor }}
                      title={isArabic ? "تعديل" : "Edit"}
                    >
                      <FiEdit2 size={16} />
                    </button>
                  )}
                  {perms.deleteProduct && (
                    <button
                      onClick={() => handleDelete(p)}
                      className="p-2 sm:p-2.5 rounded-lg text-white transition-all active:scale-105 shadow-sm flex-shrink-0"
                      style={{ background: colors.red }}
                      title={isArabic ? "حذف" : "Delete"}
                    >
                      <FiTrash2 size={16} />
                    </button>
                  )}
                </div>
              </td>
            </>
          )}
          renderCard={(p) => (
            <div className="space-y-3">
              <div className="flex gap-4 items-start">
                <img
                  src={buildImageUrl(getProductDisplayImage(p))}
                  alt=""
                  className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "https://placehold.co/120x120/e0e0e0/666?text=Product";
                  }}
                />

                <div className="flex-1">
                  <h3 className="font-bold text-lg" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                    {(() => {
                      const name = p.name || "—";
                      if (name === "—") return name;
                      const words = name.split(" ");
                      if (words.length <= 2) return name;
                      return (
                        <div className="flex flex-col">
                          <span>{words.slice(0, 2).join(" ")}</span>
                          <span className="text-sm font-normal mt-0.5" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>{words.slice(2).join(" ")}</span>
                        </div>
                      );
                    })()}
                  </h3>
                  <div className="flex gap-2 mt-1 text-sm" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                    <span>{renderText(p.type)}</span>                <span>•</span>
                    <span>{p.category}</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-between items-center pt-2 border-t" style={{ borderColor: isDark ? "rgba(255,255,255,0.1)" : "#e5e7eb" }}>
                <div>
                  <p className="text-sm" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                    {isArabic ? "السعر:" : "Price:"}
                  </p>
                  <p className="font-bold" style={{ color: colors.gold }}>
                    {p.priceMin === p.priceMax
                      ? p.priceMin
                      : `${p.priceMin} - ${p.priceMax}`
                    } {isArabic ? "د.ع" : "IQD"}
                  </p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                    {isArabic ? "التكلفة:" : "Cost:"}
                  </p>
                  <p className="font-bold" style={{ color: colors.red }}>
                    {p.costPriceMin === p.costPriceMax
                      ? p.costPriceMin
                      : `${p.costPriceMin} - ${p.costPriceMax}`
                    } {isArabic ? "د.ع" : "IQD"}
                  </p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                    {isArabic ? "عدد الطلبات:" : "Orders:"}
                  </p>
                  <p className="font-bold" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                    {p.orders || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                    {isArabic ? "الكمية:" : "Qty:"}
                  </p>
                  <p className="font-bold" style={{ color: (p.quantity === 0 || p.stockStatus === "LOW_STOCK" || p.stockStatus === "OUT_OF_STOCK") ? colors.red : (isDark ? "#f1f5f9" : "#111827") }}>
                    {p.quantity || 0}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 pt-2 flex-nowrap overflow-x-auto">
                <button
                  onClick={() => {
                    const fullItem = rawItems.find(r => r.itemId === p.itemId);
                    openModal("view", fullItem);
                  }}

                  className="flex-1 min-w-0 p-2 rounded-lg text-white text-sm transition-all active:scale-105 shadow-sm"
                  style={{ background: colors.gold, color: isDark ? "#1e293b" : "#ffffff" }}
                >
                  <FiEye className="inline mr-1" size={14} />
                  <span className="whitespace-nowrap">{isArabic ? "عرض" : "View"}</span>
                </button>
                {perms.editProduct && (
                  <button
                    onClick={() => {
                      const fullItem = rawItems.find(r => r.itemId === p.itemId);
                      openModal("edit", fullItem || p);
                    }}
                    className="flex-1 min-w-0 p-2 rounded-lg text-white text-sm transition-all active:scale-105 shadow-sm"
                    style={{ background: colors.mainColor }}
                  >
                    <FiEdit2 className="inline mr-1" size={14} />
                    <span className="whitespace-nowrap">{isArabic ? "تعديل" : "Edit"}</span>
                  </button>
                )}
                {perms.deleteProduct && (
                  <button
                    onClick={() => handleDelete(p)}
                    className="flex-1 min-w-0 p-2 rounded-lg text-white text-sm transition-all active:scale-105 shadow-sm"
                    style={{ background: colors.red }}
                  >
                    <FiTrash2 className="inline mr-1" size={14} />
                    <span className="whitespace-nowrap">{isArabic ? "حذف" : "Delete"}</span>
                  </button>
                )}
              </div>
            </div>
          )}
        />
      </div>

      {/* ===== المودال ===== */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4"
            style={{
              pointerEvents: modalOpen ? 'auto' : 'none',
              zIndex: modalOpen ? 9999 : 'unset',
              background: 'transparent',
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-3xl rounded-2xl shadow-2xl p-6 sm:p-8 relative overflow-y-auto"
              style={{
                backgroundColor: colors.cardBg,
                border: `1px solid ${colors.border}`,
                maxHeight: "90vh",
                color: colors.text,
                display: "flex",
                flexDirection: "column",
                padding: 0 // Remove padding to make header sticky to edges
              }}
            >
              {/* Header - Sticky */}
              <div className="sticky top-0 z-[100] flex items-center justify-between p-6 sm:p-8 border-b bg-inherit" style={{ borderColor: colors.border }}>
                <h2
                  className="text-lg sm:text-2xl font-bold truncate pr-2"
                  style={{ color: colors.text }}
                >
                  {modalMode === "view"
                    ? (isArabic ? "عرض المنتج" : "View Product")
                    : modalMode === "edit"
                      ? (isArabic ? "تعديل" : "Edit")
                      : (isArabic ? "جديد" : "New")
                  }

                </h2>
                <div className="flex items-center gap-3">
                  {modalMode === "view" && (
                    <button
                      onClick={() => handleOpenMovement(getItemId(current))}
                      className="p-2 sm:px-4 sm:py-2 rounded-xl text-white font-bold transition-all active:scale-105 shadow-md flex items-center gap-2"
                      style={{ backgroundColor: colors.gold }}
                      title={isArabic ? "حركة المادة" : "Product Movement"}
                    >
                      <FiActivity size={18} />
                      <span className="hidden sm:inline">{isArabic ? "حركة المادة" : "Movement"}</span>
                    </button>
                  )}
                  {modalMode !== "view" && (
                    <button
                      onClick={handleSave}
                      className="px-3 py-2 sm:px-4 sm:py-2 rounded-xl text-white font-bold transition-all active:scale-105 shadow-md"
                      style={{ backgroundColor: colors.mainColor }}
                    >
                      <span className="sm:hidden">{isArabic ? "حفظ" : "Save"}</span>
                      <span className="hidden sm:inline">{isArabic ? "حفظ التغييرات" : "Save Changes"}</span>
                    </button>
                  )}
                  <button
                    onClick={closeModal}
                    className="w-10 h-10 flex items-center justify-center rounded-xl transition-all duration-200 active:bg-opacity-10"
                    style={{
                      color: colors.subText,
                      backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "#EF4444";
                      e.currentTarget.style.backgroundColor = isDark ? "rgba(239, 68, 68, 0.1)" : "rgba(239, 68, 68, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = colors.subText;
                      e.currentTarget.style.backgroundColor = isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)";
                    }}
                  >
                    <FiX size={24} />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 sm:p-8 flex-1">

                {modalMode === "view" && current && (
                  (() => {
                    const currentTag = tags.find(t =>
                      t.id === (current.tags?.tagId ?? current.tags?.id ?? current.tagsId)
                    );

                    const currentCategory = categories.find(c =>
                      c.id === (current.category?.categoryId ?? current.category?.id ?? current.categoryId)
                    );

                    return (
                      <div className="max-w-3xl mx-auto">
                        {/* Main Image Banner */}
                        <div className="mb-6 flex justify-center">
                          <img
                            src={buildImageUrl(current.mainImageUrl || current.mainImage)}
                            className="w-full max-w-md aspect-video object-contain bg-white rounded-2xl shadow-sm border p-4"
                            style={{ borderColor: colors.border }}
                            alt="Main product"
                          />
                        </div>

                        <h3 className="text-xl sm:text-2xl font-black mb-1 text-center">
                          {current.itemName}
                        </h3>

                        <p className="text-sm opacity-80 mb-6 text-center max-w-xl mx-auto">
                          {current.description || current.itemDescription || ""}
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-5">
                          <InfoTile
                            label={isArabic ? "النوع" : "Type"}
                            value={currentTag?.name || "—"}
                            icon={<FiPackage />}
                          />

                          <InfoTile
                            label={isArabic ? "القسم" : "Category"}
                            value={currentCategory?.name || "—"}
                            icon={<FiTag />}
                          />

                          <InfoTile
                            label={isArabic ? "الخصم العام" : "Global Discount"}
                            value={current.discountValue ? `${current.discountValue} ${current.discountType === 1 ? "%" : (isArabic ? "د.ع" : "IQD")}` : "—"}
                            icon={<FiTag />}
                          />

                          <InfoTile
                            label={isArabic ? "عدد الطلبات" : "Orders"}
                            value={current.orders || 0}
                            icon={<FiBox />}
                          />
                        </div>

                        {/* صور المتغيرات */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                          {(() => {
                            const parentImage = current.mainImageUrl || current.mainImage;
                            return [...(current.variants || [])]
                              .sort((a, b) => (Number(b.stock?.quantity) || 0) - (Number(a.stock?.quantity) || 0))
                              .flatMap(v => [v.imageUrl || parentImage])
                              .filter((img, idx, self) => img && self.indexOf(img) === idx)
                              .map((img, idx) => (
                                <img
                                  key={idx}
                                  src={buildImageUrl(img)}
                                  className="w-full h-32 object-contain bg-white rounded cursor-pointer"
                                  onClick={() => setZoomImage(buildImageUrl(img))}
                                />
                              ));
                          })()}
                        </div>

                        {/* تفاصيل المتغيرات - عرض الأسعار قبل وبعد الخصم */}
                        <div className="space-y-3">
                          {(() => {
                            const groupedForView = [];
                            (current.variants || []).forEach(v => {
                              const key = `${v.variantName}-${v.price}-${v.color?.name}`;
                              let g = groupedForView.find(x => x._key === key);
                              if (!g) {
                                g = { _key: key, ...v, allSizes: [] };
                                g.stock = { ...v.stock, quantity: Number(v.stock?.quantity || 0) };
                                if (v.sizes && v.sizes.length > 0) {
                                  g.allSizes = [...v.sizes];
                                } else {
                                  g.allSizes.push({
                                    size: v.memory?.name || v.size?.name || "—",
                                    quantity: v.stock?.quantity || 0
                                  });
                                }
                                groupedForView.push(g);
                              } else {
                                if (v.sizes && v.sizes.length > 0) g.allSizes.push(...v.sizes);
                                if (g.stock) g.stock.quantity += Number(v.stock?.quantity || 0);
                              }
                            });

                            return groupedForView.map((v, idx) => {
                              // حساب السعر بعد الخصم للمتغير الحالي
                              const finalPrice = calculatePriceAfterDiscount(v.price, current.discountValue, current.discountType);
                              const hasDiscount = current.discountValue > 0;
                              const variantDisplayImage = v.imageUrl || current.mainImageUrl || current.mainImage;

                              return (
                                <div
                                  key={idx}
                                  className="border rounded-lg p-3"
                                  style={{ borderColor: colors.border }}
                                >
                                  <div className="flex gap-3 items-center">
                                    <img
                                      src={buildImageUrl(variantDisplayImage)}
                                      className="w-16 h-16 object-contain bg-white p-1 rounded"
                                      onClick={() => variantDisplayImage && setZoomImage(buildImageUrl(variantDisplayImage))}
                                      style={{ cursor: variantDisplayImage ? "pointer" : "default" }}
                                    />

                                    <div className="flex-1">
                                      <div className="font-semibold">
                                        {v.variantName || `Variant ${idx + 1}`}
                                      </div>

                                      {/* عرض الأسعار هنا */}
                                      <div className="flex flex-wrap items-center gap-2 mt-1">
                                        {hasDiscount ? (
                                          <>
                                            <span className="text-sm font-bold" style={{ color: colors.gold }}>
                                              {isArabic ? "السعر النهائي:" : "Final Price:"} {finalPrice.toLocaleString()} {isArabic ? "د.ع" : "IQD"}
                                            </span>
                                            <span className="text-xs line-through opacity-50">
                                              {v.price.toLocaleString()} {isArabic ? "د.ع" : "IQD"}
                                            </span>
                                          </>
                                        ) : (
                                          <span className="text-sm font-bold" style={{ color: colors.gold }}>
                                            {isArabic ? "السعر:" : "Price:"} {v.price.toLocaleString()} {isArabic ? "د.ع" : "IQD"}
                                          </span>
                                        )}
                                      </div>

                                      <div className="text-sm opacity-80">
                                        {isArabic ? "الكمية الكلية" : "Total Qty"}: {v.stock?.quantity ?? 0}
                                      </div>

                                      <div className="text-sm opacity-80 flex items-center gap-2">
                                        <span>{isArabic ? "اللون" : "Color"}:</span>
                                        <div className="flex items-center gap-2">
                                          <span
                                            className="inline-block w-4 h-4 rounded border"
                                            style={{ background: v.color?.name || "#000", borderColor: colors.border }}
                                          />
                                          <span>{v.color?.colorName || ""}</span>
                                        </div>
                                      </div>

                                      {/* الأحجام */}
                                      <div className="mt-2 text-sm opacity-90 bg-gray-50 dark:bg-slate-800 p-2 rounded">
                                        <span className="font-bold block mb-1">{isArabic ? "الأحجام والكميات:" : "Sizes & Quantities:"}</span>
                                        <div className="flex flex-wrap gap-2">
                                          {v.allSizes.map((sz, szIdx) => (
                                            <span key={szIdx} className="inline-flex items-center gap-1 px-2 py-1 rounded border text-xs bg-white dark:bg-slate-700">
                                              <span className="font-bold">{sz.size || "—"}</span>:
                                              <span className={(sz.quantity || 0) === 0 ? "text-red-500" : "text-green-600 dark:text-green-400"}>
                                                {sz.quantity || 0}
                                              </span>
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    );
                  })()
                )}

                {(modalMode === "create" || modalMode === "edit") && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSave(e);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && e.target.tagName !== "TEXTAREA" && e.target.type !== "submit") {
                        const focusableElements = 'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])';
                        const elements = Array.from(e.currentTarget.querySelectorAll(focusableElements));
                        const index = elements.indexOf(e.target);
                        if (index > -1 && index < elements.length - 1) {
                          e.preventDefault();
                          elements[index + 1].focus();
                        }
                      }
                    }}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-5"
                  >

                    {/* Product Main Image */}
                    <div className="sm:col-span-2 space-y-2 mb-6">
                      <label className="text-sm font-semibold block" style={{ color: colors.text }}>
                        {isArabic ? "الصورة الرئيسية للمنتج" : "Product Main Image"}
                      </label>

                      {/* الـ Label هنا يعمل كحاوية كاملة، والضغط عليه يفتح الـ input المخفي بداخلة */}
                      <label className="group relative block w-full h-64 rounded-3xl border-2 border-dashed flex items-center justify-center overflow-hidden cursor-pointer transition-all hover:bg-opacity-50"
                        style={{
                          backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                          borderColor: fieldErrors.mainImage ? "#EF4444" : colors.border
                        }}
                      >
                        {/* الـ input مخفي تماماً لمنع ظهور نصوص المتصفح "Choose file" */}
                        <input
                          type="file"
                          style={{ display: "none" }}
                          accept="image/*"
                          onChange={handleMainImageChange}
                        />

                        {form.mainImage?.existingUrl ? (
                          <div className="relative w-full h-full flex items-center justify-center bg-white dark:bg-slate-900">
                            <img
                              src={buildImageUrl(form.mainImage.existingUrl)}
                              className="max-w-full max-h-full object-contain"
                              alt="Preview"
                            />
                            {/* طبقة تظهر عند التحويم تشير لإمكانية التغيير */}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center transition-opacity text-white">
                              <FiUploadCloud size={32} />
                              <span className="text-xs mt-2">{isArabic ? "تغيير الصورة" : "Change Image"}</span>
                            </div>
                          </div>
                        ) : (
                          /* هذا الجزء هو الذي سيظهر بدلاً من الـ input التقليدي */
                          <div className="text-center transition-transform group-hover:scale-105" style={{ color: colors.subText }}>
                            <FiUploadCloud size={48} className="mx-auto mb-3 opacity-40 group-hover:opacity-100 group-hover:text-purple-500 transition-all" />
                            <p className="text-sm font-bold">{isArabic ? "اضغط لرفع الصورة" : "Click to upload"}</p>
                            <p className="text-[10px] opacity-50 mt-1">{isArabic ? "PNG, JPG حتى 5MB" : "PNG, JPG up to 5MB"}</p>
                          </div>
                        )}
                      </label>
                    </div>

                    <div className="sm:col-span-2 mb-2">
                      <label className="block mb-2 font-semibold" style={{ color: colors.text }}>
                        {isArabic ? "نوع المنتج" : "Product Type"}
                      </label>
                      <div
                        className="flex items-center gap-3 cursor-pointer group"
                        onClick={() => {
                          const newValue = !form.isSingleProduct;
                          setForm(f => ({
                            ...f,
                            isSingleProduct: newValue,
                            categoryId: newValue ? 0 : f.categoryId,
                            tagsId: newValue ? 0 : f.tagsId
                          }));
                        }}
                      >
                        <button
                          type="button"
                          className="relative w-12 h-6 rounded-full transition-all duration-300"
                          style={{
                            backgroundColor: form.isSingleProduct ? colors.mainColor : (isDark ? "#334155" : "#e2e8f0"),
                            boxShadow: form.isSingleProduct ? `0 0 10px ${colors.mainColor}40` : "none"
                          }}
                        >
                          <span
                            className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform duration-300 shadow-sm ${form.isSingleProduct ? "translate-x-6" : "translate-x-0"
                              }`}
                          />
                        </button>
                        <div
                          className={`px-4 py-1.5 rounded-xl font-bold text-xs transition-all duration-300 border ${form.isSingleProduct
                            ? "bg-opacity-10"
                            : "bg-opacity-5"
                            }`}
                          style={{
                            backgroundColor: form.isSingleProduct ? `${colors.mainColor}20` : (isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"),
                            color: form.isSingleProduct ? colors.mainColor : colors.subText,
                            borderColor: form.isSingleProduct ? `${colors.mainColor}40` : (isDark ? "#334155" : "#e2e8f0"),
                          }}
                        >
                          {form.isSingleProduct
                            ? (isArabic ? "منتج فردي (بدون قسم/براند)" : "Single Product (No Category/Brand)")
                            : (isArabic ? "منتج عادي" : "Standard Product")
                          }
                        </div>
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <TextInput
                        label={isArabic ? "اسم المنتج" : "Name"}
                        value={form.name}
                        onChange={(v) => {
                          setForm((f) => ({ ...f, name: v }));
                          if (fieldErrors.name) setFieldErrors(prev => ({ ...prev, name: false }));
                        }}
                        isDark={isDark}
                        error={fieldErrors.name}
                      />
                    </div>



                    {!form.isSingleProduct && (
                      <>
                        {/* Branding Select */}
                        <label className={fieldErrors.tagsId ? "animate-pulse" : ""}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold block" style={{ color: fieldErrors.tagsId ? "#EF4444" : colors.text }}>
                              {isArabic ? "البراند" : "Brand"}
                            </span>
                            <button
                              type="button"
                              onClick={handleGoToBrands}
                              className="p-1 rounded-full active:bg-gold/10 transition-all duration-200 active:scale-110"
                              style={{ color: colors.gold }}
                              title={isArabic ? "إضافة براند" : "Add Brand"}
                            >
                              <FiPlus size={18} strokeWidth={3} />
                            </button>
                          </div>
                          <select
                            value={form.tagsId || 0}
                            onChange={(e) => {
                              const selectedId = parseInt(e.target.value);
                              setForm(f => ({ ...f, tagsId: selectedId }));
                              if (fieldErrors.tagsId) setFieldErrors(prev => ({ ...prev, tagsId: false }));
                            }}
                            className="w-full rounded-lg px-3 py-2 outline-none transition-all duration-200 focus:ring-2"
                            style={{
                              backgroundColor: isDark ? "#1e293b" : "#f8fafc",
                              border: `1px solid ${fieldErrors.tagsId ? "#EF4444" : (isDark ? "#334155" : "#e2e8f0")}`,
                              color: colors.text,
                            }}
                          >
                            <option value={0}>{isArabic ? "اختر البراند" : "Select Brand"}</option>
                            {tags.map((tag) => (
                              <option key={tag.id} value={tag.id}>{tag.name}</option>
                            ))}
                          </select>
                        </label>

                        {/* Category Select */}
                        <label className={fieldErrors.categoryId ? "animate-pulse" : ""}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold block" style={{ color: fieldErrors.categoryId ? "#EF4444" : colors.text }}>
                              {isArabic ? "القسم" : "Category"}
                            </span>
                            <button
                              type="button"
                              onClick={handleGoToCategories}
                              className="p-1 rounded-full active:bg-gold/10 transition-all duration-200 active:scale-110"
                              style={{ color: colors.gold }}
                              title={isArabic ? "إضافة قسم" : "Add Category"}
                            >
                              <FiPlus size={18} strokeWidth={3} />
                            </button>
                          </div>
                          <select
                            value={form.categoryId || 0}
                            onChange={(e) => {
                              const selectedId = parseInt(e.target.value);
                              setForm(f => ({ ...f, categoryId: selectedId, subCategoryId: 0 })); // نصفر القسم الفرعي عند تغيير الرئيسي
                              if (fieldErrors.categoryId) setFieldErrors(prev => ({ ...prev, categoryId: false }));
                            }}
                            className="w-full rounded-lg px-3 py-2 outline-none border"
                            style={{ backgroundColor: isDark ? "#1e293b" : "#f8fafc", color: colors.text, borderColor: colors.border }}
                          >
                            <option value={0}>{isArabic ? "اختر القسم" : "Select Category"}</option>
                            {categories.map((cat) => (
                              <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                          </select>
                        </label>

                        {/* SubCategory Select - يظهر فقط إذا تم اختيار قسم رئيسي */}
                        <label>
                          <span className="text-sm font-semibold block mb-2" style={{ color: colors.text }}>
                            {isArabic ? "القسم الفرعي" : "Sub Category"}
                          </span>
                          {/* تعديل الـ Select الخاص بالقسم الفرعي في الكود */}
                          <select
                            value={form.subCategoryId || 0}
                            onChange={(e) => setForm(f => ({ ...f, subCategoryId: parseInt(e.target.value) }))}
                            className="w-full rounded-lg px-3 py-2 outline-none border disabled:opacity-50"
                            disabled={!form.categoryId}
                            style={{
                              backgroundColor: isDark ? "#1e293b" : "#f8fafc",
                              color: colors.text,
                              borderColor: colors.border
                            }}
                          >
                            <option value={0}>{isArabic ? "بدون قسم فرعي" : "No Sub-category"}</option>
                            {/* تحويل id إلى Number للتأكد من المطابقة */}
                            {categories
                              .find(c => Number(c.id) === Number(form.categoryId))
                              ?.subCategories?.map((sc) => (
                                <option key={sc.id} value={sc.id}>{sc.name}</option>
                              ))}
                          </select>
                        </label>
                      </>
                    )}



                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:col-span-2 bg-opacity-5 p-4 rounded-2xl border border-dashed" style={{ backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)", borderColor: colors.border }}>

                      {/* Discount Value Input */}
                      <div className="flex flex-col gap-2">
                        <TextInput
                          label={isArabic ? "قيمة الخصم العام" : "Global Discount Value"}
                          type="number"
                          value={form.discountValue}
                          onChange={(v) => {
                            const val = parseFloat(v) || 0;
                            setForm(f => ({ ...f, discountValue: val }));
                          }}
                          isDark={isDark}
                        />
                      </div>

                      {/* Discount Type Toggle */}
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold block mb-2" style={{ color: colors.text }}>
                          {isArabic ? "نوع الخصم العام" : "Global Discount Type"}
                        </span>
                        <div className="flex bg-opacity-5 rounded-lg p-1 border h-[42px]" style={{ backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)", borderColor: isDark ? "#334155" : "#e2e8f0" }}>
                          <button
                            type="button"
                            onClick={() => setForm(f => ({ ...f, discountType: 0 }))}
                            className={`flex-1 py-1 rounded-md text-xs font-bold transition-all ${form.discountType === 0 ? "shadow-sm" : "opacity-40"}`}
                            style={{
                              backgroundColor: form.discountType === 0 ? colors.mainColor : "transparent",
                              color: form.discountType === 0 ? "#fff" : colors.text
                            }}
                          >
                            {isArabic ? "ثابت" : "Fixed"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setForm(f => ({ ...f, discountType: 1 }))}
                            className={`flex-1 py-1 rounded-md text-xs font-bold transition-all ${form.discountType === 1 ? "shadow-sm" : "opacity-40"}`}
                            style={{
                              backgroundColor: form.discountType === 1 ? colors.mainColor : "transparent",
                              color: form.discountType === 1 ? "#fff" : colors.text
                            }}
                          >
                            {isArabic ? "% نسبة" : "% Ratio"}
                          </button>
                        </div>
                      </div>

                      {/* Preview and Validation Alert */}
                      {/* Preview and Validation Alert */}
                      <div className="sm:col-span-2 mt-2 p-3 rounded-xl border" style={{ backgroundColor: isDark ? "#0f172a" : "#fff", borderColor: colors.border }}>
                        {form.variants.map((v, idx) => {
                          const finalPrice = calculatePriceAfterDiscount(v.price, form.discountValue, form.discountType);
                          const cost = parseFloat(v.costPrice) || 0;

                          const isZeroOrLess = finalPrice <= 0 && v.price > 0;
                          const isLoss = finalPrice < cost && cost > 0; // شرط الخسارة

                          return (
                            <div key={idx} className="flex justify-between items-center mb-1 last:mb-0 border-b last:border-0 pb-1" style={{ borderColor: isDark ? "#1e293b" : "#f1f5f9" }}>
                              <span className="text-xs opacity-70">{v.variantName || `${isArabic ? "متغير" : "Variant"} ${idx + 1}`}:</span>
                              <div className="flex flex-col items-end">
                                <span
                                  className={`text-sm font-bold ${(isZeroOrLess || isLoss) ? "text-red-500" : ""}`}
                                  style={{ color: (isZeroOrLess || isLoss) ? "#ef4444" : colors.gold }}
                                >
                                  {isArabic ? "السعر النهائي: " : "Final Price: "}
                                  {finalPrice.toLocaleString()} {isArabic ? "د.ع" : "IQD"}
                                </span>

                                {isZeroOrLess && (
                                  <span className="text-[10px] text-red-500 font-bold animate-pulse">
                                    {isArabic ? "⚠️ السعر يجب أن يكون أكبر من صفر" : "⚠️ Price must be > 0"}
                                  </span>
                                )}

                                {isLoss && !isZeroOrLess && (
                                  <span className="text-[10px] text-red-500 font-bold">
                                    {isArabic ? `⚠️ خسارة! (التكلفة: ${cost.toLocaleString()})` : `⚠️ Loss! (Cost: ${cost.toLocaleString()})`}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <label className="sm:col-span-2">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold" style={{ color: colors.text }}>
                            {isArabic ? "الوصف المختصر" : "Short Description"}
                          </span>
                          <span className="text-xs opacity-60 font-normal">
                            ({isArabic ? "اختياري" : "Optional"})
                          </span>
                        </div>

                        {/* عداد الأحرف – نغير لونه فقط إذا اقترب من الحد أو تجاوزه */}
                        <span
                          className={`text-[10px] font-medium px-2 py-0.5 rounded-full transition-all ${form.description?.length > 45
                            ? "bg-red-500/10 text-red-600 border border-red-400"
                            : "bg-gray-500/10 text-gray-500 border border-transparent"
                            }`}
                        >
                          {form.description?.length || 0} / 50
                        </span>
                      </div>

                      <textarea
                        rows={2}
                        value={form.description || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val.length <= 50) {
                            setForm((f) => ({ ...f, description: val }));
                          } else {
                            // تنبيه خفيف فقط – بدون Swal إذا أردت
                            // أو اتركه كما هو
                          }
                        }}
                        placeholder={isArabic ? "اكتب وصف مختصر ... (اختياري)" : "Brief description... (optional)"}
                        className="w-full rounded-lg px-3 py-2 outline-none transition-all duration-200 focus:ring-2 resize-none border text-sm"
                        style={{
                          backgroundColor: isDark ? "#1e293b" : "#f8fafc",
                          borderColor: form.description?.length >= 48 ? "#f59e0b" : (isDark ? "#334155" : "#e2e8f0"), // لون تحذيري خفيف بدل الأحمر
                          color: colors.text,
                        }}
                      />

                      <p className="text-[10px] opacity-50 mt-1">
                        {isArabic
                          ? "اختياري – الحد الأقصى 50 حرف"
                          : "Optional – max 50 characters"}
                      </p>
                    </label>

                    <div className="sm:col-span-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-black opacity-80 uppercase tracking-tighter">
                          {isArabic ? "المتغيرات" : "Variants"}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setForm(f => ({
                              ...f,
                              variants: [
                                ...f.variants,
                                {
                                  variantName: "", price: "", costPrice: "",
                                  lowStockValue: 0,
                                  colorGroups: [
                                    {
                                      color: { name: "#000000" },
                                      image: { file: null, existingUrl: null, isChanged: false },
                                      sizes: [{ size: "", quantity: "", lowStockValue: 0 }]
                                    }
                                  ]
                                }
                              ]
                            }));
                            setExpandedVariant(form.variants.length);
                          }}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-xs font-bold transition-transform active:scale-95"
                          style={{ backgroundColor: colors.mainColor }}
                        >
                          <FiPlus />
                          {isArabic ? "متغير جديد" : "New Variant"}
                        </button>
                      </div>

                      <div className="space-y-4 mt-4">
                        {form.variants.map((v, vidx) => {
                          const isExpanded = expandedVariant === vidx;
                          return (
                            <div key={vidx} className="border rounded-2xl overflow-hidden shadow-sm transition-all" style={{ borderColor: colors.border, backgroundColor: isDark ? "#0f172a" : "#ffffff" }}>
                              {/* Variant Header */}
                              <div
                                className="p-4 flex items-center justify-between cursor-pointer select-none"
                                style={{ backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)" }}
                                onClick={() => setExpandedVariant(vidx === expandedVariant ? -1 : vidx)}
                              >
                                <div className="flex items-center gap-4">
                                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black text-white shadow-md" style={{ backgroundColor: colors.mainColor }}>
                                    {vidx + 1}
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="font-black text-base">{v.variantName || (isArabic ? "متغير جديد" : "New Variant")}</span>
                                    {v.price && (
                                      <span className="text-xs font-bold opacity-60" style={{ color: colors.gold }}>
                                        {v.price} {isArabic ? "د.ع" : "IQD"}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-4">
                                  {form.variants.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const updated = [...form.variants];
                                        updated.splice(vidx, 1);
                                        setForm(f => ({ ...f, variants: updated }));
                                      }}
                                      className="p-2 rounded-xl text-red-500 active:bg-red-50 transition-colors"
                                    >
                                      <FiTrash2 size={20} />
                                    </button>
                                  )}
                                  <div className={`p-2 transition-transform duration-300 ${isExpanded ? "rotate-45" : ""}`} style={{ color: colors.mainColor }}>
                                    <FiPlus size={24} />
                                  </div>
                                </div>
                              </div>

                              {/* Variant Content */}
                              {isExpanded && (
                                <div className="p-6 space-y-6">
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <TextInput
                                      label={isArabic ? "اسم المتغير" : "Variant Name"}
                                      value={v.variantName}
                                      onChange={(val) => {
                                        const updated = [...form.variants];
                                        updated[vidx].variantName = val;
                                        setForm(f => ({ ...f, variants: updated }));
                                        if (fieldErrors[`variants[${vidx}].variantName`]) {
                                          const newErrs = { ...fieldErrors };
                                          delete newErrs[`variants[${vidx}].variantName`];
                                          setFieldErrors(newErrs);
                                        }
                                      }}
                                      isDark={isDark}
                                      error={fieldErrors[`variants[${vidx}].variantName`]}
                                    />
                                    <TextInput
                                      label={<span className="text-red-500 font-bold">{isArabic ? "سعر الكلفة" : "Cost Price"}</span>}
                                      type="number"
                                      value={v.costPrice}
                                      onChange={(val) => {
                                        const updated = [...form.variants];
                                        updated[vidx].costPrice = val;
                                        setForm(f => ({ ...f, variants: updated }));
                                        if (fieldErrors[`variants[${vidx}].costPrice`]) {
                                          const newErrs = { ...fieldErrors };
                                          delete newErrs[`variants[${vidx}].costPrice`];
                                          setFieldErrors(newErrs);
                                        }
                                      }}
                                      isDark={isDark}
                                      error={fieldErrors[`variants[${vidx}].costPrice`]}
                                    />
                                    <TextInput
                                      label={isArabic ? "سعر البيع" : "Selling Price"}
                                      type="number"
                                      value={v.price}
                                      onChange={(val) => {
                                        const updated = [...form.variants];
                                        updated[vidx].price = val;
                                        setForm(f => ({ ...f, variants: updated }));
                                        if (fieldErrors[`variants[${vidx}].price`]) {
                                          const newErrs = { ...fieldErrors };
                                          delete newErrs[`variants[${vidx}].price`];
                                          setFieldErrors(newErrs);
                                        }
                                      }}
                                      isDark={isDark}
                                      error={fieldErrors[`variants[${vidx}].price`]}
                                    />
                                  </div>

                                  {/* Color Groups */}
                                  <div className="space-y-6 pt-6 border-t border-dashed" style={{ borderColor: colors.border }}>
                                    <div className="flex justify-between items-center">
                                      <span className="text-sm font-black opacity-80 uppercase tracking-wider flex items-center gap-2">
                                        <div className="w-2 h-6 rounded-full" style={{ backgroundColor: colors.gold }} />
                                        {isArabic ? "الألوان والتفاصيل" : "Colors & Details"}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const updated = [...form.variants];
                                          updated[vidx].colorGroups.push({
                                            color: { name: "#000000", colorName: "" },
                                            image: { file: null, existingUrl: null, isChanged: false },
                                            sizes: [{ size: "", quantity: "", lowStockValue: 0 }]
                                          });
                                          setForm(f => ({ ...f, variants: updated }));
                                        }}
                                        className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-white font-bold text-xs shadow-sm active:scale-105 transition-transform"
                                        style={{ backgroundColor: colors.gold }}
                                      >
                                        <FiPlus />
                                        {isArabic ? "إضافة لون جديد" : "Add New Color"}
                                      </button>
                                    </div>

                                    <div className="space-y-4">
                                      {v.colorGroups.map((cg, cgIdx) => (
                                        <div key={cgIdx} className="p-5 rounded-2xl border-2 transition-all" style={{ borderColor: isDark ? "#1e293b" : "#f1f5f9", backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)" }}>
                                          {/* Color Header: Color Picker + Image Upload + Delete Color */}
                                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 pb-4 border-b border-dashed" style={{ borderColor: isDark ? "#334155" : "#e2e8f0" }}>
                                            <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                                              {/* Improved Color Picker */}
                                              <div className="flex flex-col gap-1 w-full sm:min-w-[240px] flex-1">
                                                <span className="text-[10px] font-black opacity-40 uppercase tracking-widest">{isArabic ? "إعدادات اللون" : "Color Settings"}</span>
                                                <div className="flex items-center gap-2">
                                                  {/* منتقي اللون (دائرة) */}
                                                  <input
                                                    type="color"
                                                    value={cg.color?.name || "#000000"}
                                                    onChange={(e) => updateColorHex(vidx, cgIdx, e.target.value)}
                                                    className="w-10 h-10 rounded-full border-none cursor-pointer"
                                                  />
                                                  <input
                                                    type="text"
                                                    value={cg.color?.colorName || ""}
                                                    onChange={(e) => updateColorName(vidx, cgIdx, e.target.value)}
                                                    placeholder={isArabic ? "اسم اللون (اختياري)" : "Color Name (Optional)"}
                                                    className="flex-1 min-w-[100px] rounded-lg px-3 py-2 text-xs border bg-transparent outline-none focus:border-purple-500"
                                                    style={{ borderColor: isDark ? "#334155" : "#e2e8f0", color: colors.text }}
                                                  />
                                                </div>
                                              </div>

                                              {/* Image Upload for Color Group */}
                                              <div className="flex flex-col gap-1">
                                                <span className="text-[10px] font-black opacity-40 uppercase tracking-widest" style={{ color: colors.text }}>
                                                  {isArabic ? "الصورة" : "Image"}
                                                </span>
                                                <label className="cursor-pointer group/upload relative block">
                                                  <input
                                                    type="file"
                                                    accept="image/*"
                                                    style={{ display: "none" }}
                                                    onChange={(e) => {
                                                      const file = e.target.files[0];
                                                      if (file) {
                                                        const url = URL.createObjectURL(file);
                                                        setForm(f => {
                                                          const newVariants = [...f.variants];
                                                          const newV = { ...newVariants[vidx] };
                                                          newV.colorGroups = [...newV.colorGroups];
                                                          newV.colorGroups[cgIdx] = {
                                                            ...newV.colorGroups[cgIdx],
                                                            image: { file, existingUrl: url, isChanged: true }
                                                          };
                                                          newVariants[vidx] = newV;
                                                          return { ...f, variants: newVariants };
                                                        });
                                                      }
                                                    }}
                                                  />
                                                  <div className="w-14 h-14 rounded-2xl border-2 border-dashed flex items-center justify-center overflow-hidden transition-all hover:border-gold"
                                                    style={{
                                                      borderColor: isDark ? "#334155" : "#e2e8f0",
                                                      backgroundColor: isDark ? "#131c2e" : "#fff"
                                                    }}>
                                                    {cg.image?.existingUrl ? (
                                                      <img
                                                        src={cg.image?.file ? cg.image.existingUrl : buildImageUrl(cg.image.existingUrl)}
                                                        className="w-full h-full object-cover"
                                                        alt=""
                                                      />
                                                    ) : (
                                                      <FiUploadCloud size={20} className="opacity-30 group-hover/upload:text-gold transition-all" />
                                                    )}
                                                  </div>
                                                </label>
                                              </div>
                                            </div>

                                            {v.colorGroups.length > 1 && (
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  setForm(f => {
                                                    const newVariants = [...f.variants];
                                                    const newV = { ...newVariants[vidx] };
                                                    newV.colorGroups = [...newV.colorGroups];
                                                    newV.colorGroups.splice(cgIdx, 1);
                                                    newVariants[vidx] = newV;
                                                    return { ...f, variants: newVariants };
                                                  });
                                                }}
                                                className="p-2 px-3 rounded-xl text-red-500 active:bg-red-50 transition-all flex items-center gap-2 text-[10px] font-black self-end sm:self-auto uppercase tracking-widest bg-red-500/5 sm:bg-transparent"
                                              >
                                                <FiTrash2 size={16} />
                                                <span className="sm:hidden">{isArabic ? "حذف اللون" : "Delete"}</span>
                                              </button>
                                            )}
                                          </div>

                                          {/* Sizes List */}
                                          <div className="space-y-3">
                                            {cg.sizes.map((sz, szIdx) => (
                                              <div key={szIdx} className="flex flex-wrap sm:flex-nowrap items-end gap-2 sm:gap-4 bg-opacity-5 p-2 rounded-lg" style={{ backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)" }}>
                                                <div className="flex-1 min-w-[80px]">
                                                  {szIdx === 0 && <span className="text-[10px] font-black opacity-40 uppercase block mb-1 tracking-widest">{isArabic ? "الحجم" : "Size"}</span>}
                                                  <input
                                                    type="text"
                                                    value={sz.size}
                                                    onChange={(e) => {
                                                      const val = e.target.value;
                                                      setForm(f => {
                                                        const newVariants = [...f.variants];
                                                        const newV = { ...newVariants[vidx] };
                                                        newV.colorGroups = [...newV.colorGroups];
                                                        const newCG = { ...newV.colorGroups[cgIdx] };
                                                        newCG.sizes = [...newCG.sizes];
                                                        newCG.sizes[szIdx] = { ...newCG.sizes[szIdx], size: val };
                                                        newV.colorGroups[cgIdx] = newCG;
                                                        newVariants[vidx] = newV;
                                                        return { ...f, variants: newVariants };
                                                      });
                                                      if (fieldErrors[`variants[${vidx}].colorGroups[${cgIdx}].sizes[${szIdx}].size`]) {
                                                        const newErrs = { ...fieldErrors };
                                                        delete newErrs[`variants[${vidx}].colorGroups[${cgIdx}].sizes[${szIdx}].size`];
                                                        setFieldErrors(newErrs);
                                                      }
                                                    }}
                                                    placeholder={isArabic ? "الحجم" : "Size"}
                                                    className="w-full rounded-xl px-3 py-2 outline-none border-2 font-bold text-xs"
                                                    style={{
                                                      backgroundColor: isDark ? "#131c2e" : "#fff",
                                                      borderColor: fieldErrors[`variants[${vidx}].colorGroups[${cgIdx}].sizes[${szIdx}].size`] ? "#EF4444" : (isDark ? "#1e293b" : "#f1f5f9"),
                                                      color: colors.text
                                                    }}
                                                  />
                                                </div>
                                                <div className="flex-1 min-w-[60px]">
                                                  {szIdx === 0 && <span className="text-[10px] font-black opacity-40 uppercase block mb-1 tracking-widest">{isArabic ? "الكمية" : "Qty"}</span>}
                                                  <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    value={sz.quantity}
                                                    onChange={(e) => {
                                                      const val = e.target.value.replace(/[^0-9]/g, '');
                                                      setForm(f => {
                                                        const newVariants = [...f.variants];
                                                        const newV = { ...newVariants[vidx] };
                                                        newV.colorGroups = [...newV.colorGroups];
                                                        const newCG = { ...newV.colorGroups[cgIdx] };
                                                        newCG.sizes = [...newCG.sizes];
                                                        newCG.sizes[szIdx] = { ...newCG.sizes[szIdx], quantity: val };
                                                        newV.colorGroups[cgIdx] = newCG;
                                                        newVariants[vidx] = newV;
                                                        return { ...f, variants: newVariants };
                                                      });
                                                      if (fieldErrors[`variants[${vidx}].colorGroups[${cgIdx}].sizes[${szIdx}].quantity`]) {
                                                        const newErrs = { ...fieldErrors };
                                                        delete newErrs[`variants[${vidx}].colorGroups[${cgIdx}].sizes[${szIdx}].quantity`];
                                                        setFieldErrors(newErrs);
                                                      }
                                                    }}
                                                    onWheel={(e) => e.target.blur()}
                                                    placeholder="0"
                                                    className="w-full rounded-xl px-3 py-2 outline-none border-2 font-bold text-xs"
                                                    style={{
                                                      backgroundColor: isDark ? "#131c2e" : "#fff",
                                                      borderColor: fieldErrors[`variants[${vidx}].colorGroups[${cgIdx}].sizes[${szIdx}].quantity`] ? "#EF4444" : (isDark ? "#1e293b" : "#f1f5f9"),
                                                      color: colors.text
                                                    }}
                                                  />
                                                </div>
                                                <div className="flex-1 min-w-[80px]">
                                                  {szIdx === 0 && <span className="text-[10px] font-black opacity-40 uppercase block mb-1 tracking-widest text-red-500">{isArabic ? "تنبيه نقص المخزون" : "Low Stock"}</span>}
                                                  <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    value={sz.lowStockValue}
                                                    onChange={(e) => {
                                                      const val = e.target.value.replace(/[^0-9]/g, '');
                                                      setForm(f => {
                                                        const newVariants = [...f.variants];
                                                        const newV = { ...newVariants[vidx] };
                                                        newV.colorGroups = [...newV.colorGroups];
                                                        const newCG = { ...newV.colorGroups[cgIdx] };
                                                        newCG.sizes = [...newCG.sizes];
                                                        newCG.sizes[szIdx] = { ...newCG.sizes[szIdx], lowStockValue: val };
                                                        newV.colorGroups[cgIdx] = newCG;
                                                        newVariants[vidx] = newV;
                                                        return { ...f, variants: newVariants };
                                                      });
                                                    }}
                                                    onWheel={(e) => e.target.blur()}
                                                    placeholder="0"
                                                    className="w-full rounded-xl px-3 py-2 outline-none border-2 font-bold text-xs"
                                                    style={{
                                                      backgroundColor: isDark ? "#131c2e" : "#fff",
                                                      borderColor: isDark ? "#1e293b" : "#f1f5f9",
                                                      color: colors.text
                                                    }}
                                                  />
                                                </div>
                                                <div className="flex gap-1 pb-0.5">
                                                  {cg.sizes.length > 1 && (
                                                    <button
                                                      type="button"
                                                      onClick={() => {
                                                        setForm(f => {
                                                          const newVariants = [...f.variants];
                                                          const newV = { ...newVariants[vidx] };
                                                          newV.colorGroups = [...newV.colorGroups];
                                                          const newCG = { ...newV.colorGroups[cgIdx] };
                                                          newCG.sizes = [...newCG.sizes];
                                                          newCG.sizes.splice(szIdx, 1);
                                                          newV.colorGroups[cgIdx] = newCG;
                                                          newVariants[vidx] = newV;
                                                          return { ...f, variants: newVariants };
                                                        });
                                                      }}
                                                      className="p-2.5 rounded-xl text-red-400 active:text-red-500"
                                                    >
                                                      <FiX size={18} />
                                                    </button>
                                                  )}
                                                  {szIdx === cg.sizes.length - 1 && (
                                                    <button
                                                      type="button"
                                                      onClick={() => {
                                                        setForm(f => {
                                                          const newVariants = [...f.variants];
                                                          const newV = { ...newVariants[vidx] };
                                                          newV.colorGroups = [...newV.colorGroups];
                                                          const newCG = { ...newV.colorGroups[cgIdx] };
                                                          newCG.sizes = [...newCG.sizes, { size: "", quantity: "", lowStockValue: "" }];
                                                          newV.colorGroups[cgIdx] = newCG;
                                                          newVariants[vidx] = newV;
                                                          return { ...f, variants: newVariants };
                                                        });
                                                      }}
                                                      className="p-2.5 rounded-xl text-gold active:bg-gold/10 transition-colors"
                                                      title={isArabic ? "إضافة حجم" : "Add Size"}
                                                    >
                                                      <FiPlus size={20} strokeWidth={3} />
                                                    </button>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>


                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Zoom Image Modal */}
      <AnimatePresence>
        {zoomImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
            style={{
              pointerEvents: zoomImage ? 'auto' : 'none',
              zIndex: zoomImage ? 10000 : 'unset',
              background: 'transparent',
            }}
            onClick={() => setZoomImage(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={zoomImage}
                alt="Zoomed"
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                style={{
                  backgroundColor: "#ffffff",
                  padding: "12px",
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
                }}
              />
              <button
                onClick={() => setZoomImage(null)}
                className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full text-white bg-black/50 active:bg-black/70 transition-all duration-200 backdrop-blur-sm"
                style={{
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                }}
              >
                <FiX size={24} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </PageContainer>
  );
}
const renderText = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string" || typeof v === "number") return v;
  if (typeof v === "object") return v.name ?? "—";
  return "—";
};


/* ==== Input + InfoTile ==== */
const TextInput = ({ label, value, onChange, type = "text", isDark, error, placeholder }) => {
  const { colors } = useUI();

  const handleInputChange = (e) => {
    let val = e.target.value;
    if (type === "number") {
      // Allow only numbers and one decimal point
      val = val.replace(/[^0-9.]/g, '');
      const parts = val.split('.');
      if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
    }
    onChange(val);
  };

  return (
    <label className="block">
      <span className="text-sm font-semibold block mb-2" style={{ color: error ? "#EF4444" : colors.text }}>
        {label}
      </span>
      <input
        type={type === "number" ? "text" : type}
        inputMode={type === "number" ? "decimal" : undefined}
        value={value || ""}
        placeholder={placeholder}
        onChange={handleInputChange}
        onWheel={(e) => e.target.blur()}
        className="w-full rounded-lg px-3 py-2 outline-none transition-all duration-200 focus:ring-2 resize-none"
        style={{
          backgroundColor: isDark ? "#1e293b" : "#f8fafc",
          border: `1px solid ${error ? "#EF4444" : (isDark ? "#334155" : "#e2e8f0")}`,
          color: isDark ? "#f1f5f9" : "#111827",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = colors.inputFocus;
          e.target.style.boxShadow = `0 0 0 2px ${colors.inputFocus}20`;
        }}
        onBlur={(e) => {
          e.target.style.borderColor = error ? "#EF4444" : colors.inputBorder;
          e.target.style.boxShadow = "none";
        }}
      />
    </label>
  );
}

function InfoTile({ label, value, icon }) {
  const { colors } = useUI();
  return (
    <div
      className="rounded-lg border p-3 flex items-center gap-3 transition-all duration-200"
      style={{
        borderColor: colors.border,
        backgroundColor: colors.tableHeaderBg,
      }}
    >
      <div className="text-xl" style={{ color: colors.mainColor }}>{icon}</div>
      <div>
        <div className="text-xs" style={{ color: colors.subText }}>{label}</div>
        <div className="font-semibold" style={{ color: colors.text }}>{value}</div>
      </div>
    </div>
  );
}
